import config from '../../config';
import { getAuthHeaders, handleResponse } from './common';

export function stripLetterTemplateHtml(html) {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
}

export function letterTemplateHasContent(html) {
  return stripLetterTemplateHtml(html).trim().length > 0;
}

export function normalizeLetterTemplateContent(content) {
  if (!content) return '';
  if (/<\/?[a-z][\s\S]*>/i.test(content)) return content;
  return content
    .split('\n')
    .map((line) => `<p>${line || '<br>'}</p>`)
    .join('');
}

export function validateLetterTemplateContent(content, validFieldKeys = []) {
  const issues = [];

  if (!letterTemplateHasContent(content)) {
    issues.push('Template content cannot be empty');
    return issues;
  }

  const textForValidation = stripLetterTemplateHtml(content);

  if (textForValidation.match(/\{[^}]*$/) || textForValidation.match(/^[^{]*\}/)) {
    issues.push('Template contains malformed placeholders (missing opening or closing braces)');
  }

  const validKeys = new Set(validFieldKeys);
  const placeholders = textForValidation.match(/\{([^}]+)\}/g) || [];

  placeholders.forEach((placeholder) => {
    const fieldKey = placeholder.slice(1, -1);
    if (!validKeys.has(fieldKey)) {
      issues.push(`Unknown field placeholder: ${placeholder}`);
    }
  });

  return issues;
}

const letterTemplatesApi = {
  listTemplates: async ({ includeArchived = false, sortBy, sortDir } = {}) => {
    const params = new URLSearchParams();
    if (includeArchived) params.append('includeArchived', 'true');
    if (sortBy) params.append('sortBy', sortBy);
    if (sortDir) params.append('sortDir', sortDir);
    const qs = params.toString();
    const res = await fetch(
      `${config.api.url}/letter-templates${qs ? `?${qs}` : ''}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(res);
  },

  getTemplate: async (id) => {
    const res = await fetch(
      `${config.api.url}/letter-templates/${id}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(res);
  },

  createTemplate: async (payload) => {
    const res = await fetch(
      `${config.api.url}/letter-templates`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      }
    );
    return handleResponse(res);
  },

  updateTemplate: async (id, payload) => {
    const res = await fetch(
      `${config.api.url}/letter-templates/${id}`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      }
    );
    return handleResponse(res);
  },

  archiveTemplate: async (id) => {
    const res = await fetch(
      `${config.api.url}/letter-templates/${id}/archive`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
      }
    );
    return handleResponse(res);
  },

  restoreTemplate: async (id) => {
    const res = await fetch(
      `${config.api.url}/letter-templates/${id}/restore`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
      }
    );
    return handleResponse(res);
  },

  copyTemplate: async (id, payload) => {
    const res = await fetch(
      `${config.api.url}/letter-templates/${id}/copy`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      }
    );
    return handleResponse(res);
  },

  getFields: async () => {
    const res = await fetch(
      `${config.api.url}/letter-templates/fields`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(res);
  },

  renderTemplate: async (payload) => {
    const res = await fetch(
      `${config.api.url}/letter-templates/render`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      }
    );
    return handleResponse(res);
  },

  generatePdf: async (payload) => {
    const res = await fetch(
      `${config.api.url}/letter-templates/generate-pdf`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      }
    );

    if (!res.ok) {
      const errorText = await res.text();
      throw new Error(errorText || `PDF generation failed: ${res.status}`);
    }

    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename[^;=\n]*=["']?([^"';\n]+)/);
    const filename = match ? match[1] : 'letter.pdf';

    return { blob, filename };
  },

  getClientLetters: async (clientReferenceId) => {
    const res = await fetch(
      `${config.api.url}/clients/${clientReferenceId}/letters`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(res);
  },

  getClientLetter: async (clientReferenceId, letterReferenceId) => {
    const res = await fetch(
      `${config.api.url}/clients/${clientReferenceId}/letters/${letterReferenceId}`,
      { headers: getAuthHeaders() }
    );
    // Return null for unsupported/missing routes instead of throwing.
    if (res.status === 404 || res.status === 405) {
      return null;
    }
    return handleResponse(res);
  },

  createClientLetter: async (clientReferenceId, payload) => {
    const res = await fetch(
      `${config.api.url}/clients/${clientReferenceId}/letters`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      }
    );
    return handleResponse(res);
  },

  updateClientLetter: async (clientReferenceId, letterReferenceId, payload) => {
    const res = await fetch(
      `${config.api.url}/clients/${clientReferenceId}/letters/${letterReferenceId}`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      }
    );
    return handleResponse(res);
  },

  deleteClientLetter: async (clientReferenceId, letterReferenceId) => {
    const res = await fetch(
      `${config.api.url}/clients/${clientReferenceId}/letters/${letterReferenceId}`,
      { method: 'DELETE', headers: getAuthHeaders() }
    );
    return handleResponse(res);
  },

  /** Finalizes an existing client letter. */
  finalizeClientLetter: async (clientReferenceId, letterReferenceId) => {
    const res = await fetch(
      `${config.api.url}/clients/${clientReferenceId}/letters/${letterReferenceId}/finalize`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
      }
    );
    return handleResponse(res);
  },
};

export default letterTemplatesApi;
